import { Dialog, Transition } from '@headlessui/react'
import { Button, Input, Option, Select } from '@material-tailwind/react'
import { Fragment, useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { AtSymbolIcon, LockClosedIcon, UserCircleIcon } from '@heroicons/react/solid'
import { P } from '../../../components/typography/typography'
import userService from '../../../Services/user.service'
import practiceService, { Practice } from '../../../Services/practice.services'

// The practice dropdown's value when no practice is chosen
const NO_PRACTICE = 'none'

interface FormCreateDentist {
  firstName: string
  lastName: string
  email: string
  pass: string
  confirmPass: string
}

// Same fields and rules as the dentist's own sign up form (src/screens/auth/signUpForm.tsx)
const schema = yup.object().shape({
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  email: yup.string().email('Email format is invalid').required('Email is required'),
  pass: yup.string().required('Password is required').min(6, 'Must contain at least 6 characters'),
  confirmPass: yup.string().required('Confirm password is required').oneOf([yup.ref('pass'), null], 'Password and Confirm Password must match')
})

const CreateDentistDialog = (props: {
  isOpen: boolean
  onClose: () => void
  // Called once the dentist exists; startCredentialing is which button was pressed
  onCreated: (email: string, startCredentialing: boolean) => void
}) => {
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [practices, setPractices] = useState<Practice[]>([])
  const [practiceId, setPracticeId] = useState(NO_PRACTICE)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormCreateDentist>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  // Start each opening with an empty form
  useEffect(() => {
    if (props.isOpen) {
      reset()
      setErrorMessage('')
      setPracticeId(NO_PRACTICE)
      getPractices()
    }
  }, [props.isOpen])

  const getPractices = async () => {
    const result = await practiceService.GetPractices()
    if (!result.success) {
      setErrorMessage('Could not load practices')
    }
    setPractices(result.data)
  }

  const onSubmit = async (values: FormCreateDentist, startCredentialing: boolean) => {
    setErrorMessage('')
    setIsSubmitting(true)
    try {
      const result = await userService.CreateDentist({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.pass,
        ...(practiceId !== NO_PRACTICE && { practiceId: Number(practiceId) }),
      })
      if (!result?.success) {
        setErrorMessage('The dentist could not be created, please try again')
        return
      }
      props.onCreated(values.email, startCredentialing)
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error ?? 'Connection error has occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Transition appear show={props.isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={props.onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h1" className="font-medium leading-tight text-2xl mt-0 mb-2 text-black-600">
                  Create dentist
                </Dialog.Title>
                <p className="mb-4 text-slate-500">
                  No verification email is sent. Give the dentist this password so they can log in.
                </p>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="flex flex-col gap-4">
                    <Input label="First Name" icon={<UserCircleIcon className="text-blue-600" />} {...register('firstName')} />
                    <Input label="Last Name" icon={<UserCircleIcon className="text-blue-600" />} {...register('lastName')} />
                    <Input label="Email" icon={<AtSymbolIcon className="text-blue-600" />} {...register('email')} />
                    <Input label="Password" type="password" icon={<LockClosedIcon className="text-blue-600" />} {...register('pass')} />
                    <Input label="Confirm Password" type="password" icon={<LockClosedIcon className="text-blue-600" />} {...register('confirmPass')} />
                    <Select
                      key={practices.map((practice) => practice.id).join()}
                      label="Practice"
                      value={practiceId}
                      onChange={(value) => setPracticeId(value ? String(value) : NO_PRACTICE)}>
                      <Option value={NO_PRACTICE}>None</Option>
                      {practices.map((practice) => (
                        <Option key={practice.id} value={String(practice.id)}>
                          {`${practice.name} (${practice.stateName})`}
                        </Option>
                      ))}
                    </Select>
                    {errors.firstName?.message && <P className="text-red-600">{errors.firstName?.message}</P>}
                    {errors.lastName?.message && <P className="text-red-600">{errors.lastName?.message}</P>}
                    {errors.email?.message && <P className="text-red-600">{errors.email?.message}</P>}
                    {errors.pass?.message && <P className="text-red-600">{errors.pass?.message}</P>}
                    {errors.confirmPass?.message && <P className="text-red-600">{errors.confirmPass?.message}</P>}
                    {errorMessage && <P className="text-red-600">{errorMessage}</P>}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 pt-6 mt-6 border-t border-solid border-slate-200">
                    <button
                      className="text-blue-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none"
                      type="button"
                      onClick={props.onClose}
                    >
                      Cancel
                    </button>
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={isSubmitting}
                      onClick={handleSubmit((values) => onSubmit(values, false))}
                    >
                      Create dentist
                    </Button>
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={handleSubmit((values) => onSubmit(values, true))}
                    >
                      Create and start their credentialing
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default CreateDentistDialog
